import { type FC, memo, useCallback, useRef, useState } from "react";
import { cx } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetHeader,
  Modal,
  ModalBody,
  ModalHeader,
  ModalMobileVariant,
} from "@stubhub/broadway-web";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { BannerCard } from "./BannerCard";
import { DetailBody } from "./DetailBody";
import { bannerButton, detailModalContent } from "./styles";

type FanProtectBannerMode = "full" | "compact" | "responsive";

interface Props {
  mode?: FanProtectBannerMode | undefined;
  className?: string | undefined;
}

export const FanProtectBanner: FC<Props> = memo(({ mode = "responsive", className }) => {
  const storePageEvent = useStorePageEvent();
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  // "responsive" delegates layout to the component, "full"/"compact" lets callers pin a layout regardless of viewport
  const effectiveMode = mode === "responsive" ? (isDesktop ? "full" : "compact") : mode;
  const hasTrackedModuleView = useRef(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const handleModuleView = useCallback(() => {
    if (hasTrackedModuleView.current) return;
    hasTrackedModuleView.current = true;
    storePageEvent(PE_TYPE.LOGGING, PE_KEYS.FAN_PROTECTION_MODULE_VIEW, effectiveMode);
  }, [effectiveMode, storePageEvent]);

  const handleOpen = useCallback(() => {
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.FAN_PROTECT_MODULE_CLICK, effectiveMode);
    setIsDetailOpen(true);
  }, [effectiveMode, storePageEvent]);

  const handleClose = useCallback(() => {
    if (isDetailOpen) {
      storePageEvent(PE_TYPE.CLOSE, PE_KEYS.FAN_PROTECTION_SHEET_CLOSE, effectiveMode);
    }
    setIsDetailOpen(false);
  }, [isDetailOpen, effectiveMode, storePageEvent]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose],
  );

  // render modal for desktop
  if (effectiveMode === "full") {
    return (
      <>
        <InViewTracker onInView={handleModuleView} />
        <button type="button" className={cx(bannerButton, className)} onClick={handleOpen} aria-expanded={isDetailOpen}>
          <BannerCard />
        </button>
        <Modal
          isOpen={isDetailOpen}
          onOpenChange={handleOpenChange}
          dismissible
          mobileVariant={ModalMobileVariant.Dialog}
          classes={{ container: detailModalContent }}
        >
          <ModalHeader />
          <ModalBody>
            <DetailBody />
          </ModalBody>
        </Modal>
      </>
    );
  }

  // render bottom sheet for mobile
  return (
    <>
      <InViewTracker onInView={handleModuleView} />
      <button type="button" className={cx(bannerButton, className)} onClick={handleOpen} aria-expanded={isDetailOpen}>
        <BannerCard />
      </button>
      <BottomSheet isOpen={isDetailOpen} onOpenChange={handleOpenChange}>
        <BottomSheetHeader />
        <BottomSheetBody>
          <DetailBody />
        </BottomSheetBody>
      </BottomSheet>
    </>
  );
});

FanProtectBanner.displayName = "FanProtectBanner";
